import { productImageAlt } from "@/lib/imageAlt";
import { useParams, Link, useLocation, useNavigationType, useSearchParams } from "react-router-dom";
import { GuideLinks } from "@/components/GuideLinks";
import { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { fetchProductsWithPagination, fetchProductSlices, fetchFacetSlices, fetchProductCount, ShopifyProduct, formatPrice, getShopifyImageUrl, type FetchProductsResult, type FacetProduct } from "@/lib/shopify";
import { exactTypeClause, exactTypeQuery, keepExactTypes, typeStock } from "@/lib/productTypes";
import { listingPrice } from "@/lib/variants";
import { getCategoryBySlug, getDescendantCategories, productTypeLabel } from "@/lib/categories";
import NotFound from "@/pages/NotFound";
import { extractFiltersFromProducts, productMatchesFilter, getStaticProductTypeFilters, getShopifyProductTypeFromLabel, buildFacetQuery, mapSortToShopify, isServerFacet } from "@/lib/productFilters";
import { ProductCard } from "@/components/ProductCard";
import { dedupeProducts, uniqueDisplayNames, cleanProductTitle, parseTitleAttributes } from "@/lib/productTitle";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ProductCardSkeleton } from "@/components/ProductCardSkeleton";
import { CategoryRoomNav } from "@/components/CategoryRoomNav";
import { CategoryFilterSidebar, SortOption, FilterGroup } from "@/components/CategoryFilterSidebar";
import { OGTags } from "@/components/OGTags";
import { Helmet } from "react-helmet-async";
import { extractCategoryFacts, categoryIntro } from "@/lib/seo";
import { categoryHeadTitle, categoryHeadDescription } from "../../scripts/category-meta.mjs";
import { categorySchemas } from "../../scripts/page-schemas.mjs";
import { stripVendors } from "../../scripts/product-meta.mjs";
import { CATEGORY_STOCK } from "@/data/categoryStock";
import { SlidersHorizontal, X, Loader2, LayoutGrid, List, Ruler } from "lucide-react";
import { CATEGORY_TOOL } from "../../scripts/category-tools.mjs";
import { Button } from "@/components/ui/button";
import { useActivePromotion, getCategoryDiscount } from "@/hooks/useActivePromotion";
import { inferProductType } from "@/lib/productFilters";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollRevealCard } from "@/components/ScrollRevealCard";
import { ProductPagination } from "@/components/ProductPagination";
import { recordCategoryStock } from "@/lib/categoryStock";
import { isSeasonalProduct } from "@/lib/railDiversity";


/**
 * Round-robin interleave products by productType so the grid feels diverse.
 * `priority` (when given) fixes the order the buckets are drawn in — used so a
 * multi-type category leads with its headline types instead of whichever type
 * happens to have the most stock (Rugs was 100% doormats before this).
 */
function interleaveByType(products: ShopifyProduct[], priority?: string[]): ShopifyProduct[] {
  const buckets = new Map<string, ShopifyProduct[]>();
  for (const p of products) {
    const type = p.node.productType || inferProductType(p.node.title, p.node.description || "") || "_other";
    if (!buckets.has(type)) buckets.set(type, []);
    buckets.get(type)!.push(p);
  }
  const queues = Array.from(buckets.entries());
  if (queues.length <= 1) return sinkSeasonal(products);
  if (priority?.length) {
    const rank = (t: string) => {
      const i = priority.findIndex((p) => p.toLowerCase() === t.toLowerCase());
      return i === -1 ? priority.length : i;
    };
    queues.sort((a, b) => rank(a[0]) - rank(b[0]) || b[1].length - a[1].length);
  } else {
    // Sort queues by size descending so largest types go first
    queues.sort((a, b) => b[1].length - a[1].length);
  }
  const result: ShopifyProduct[] = [];
  let idx = 0;
  while (result.length < products.length) {
    let added = false;
    for (const [, q] of queues) {
      if (q.length > idx) {
        result.push(q[idx]);
        added = true;
      }
    }
    if (!added) break;
    idx++;
  }
  return sinkSeasonal(result);
}

/**
 * Seasonal items stay browsable, filterable and in the sitemap — they are just
 * moved to the end of a category's ordering so Christmas doormats never front
 * the "Rugs" shelf in August.
 */
function sinkSeasonal(products: ShopifyProduct[]): ShopifyProduct[] {
  const evergreen: ShopifyProduct[] = [];
  const seasonal: ShopifyProduct[] = [];
  for (const p of products) (isSeasonalProduct(p) ? seasonal : evergreen).push(p);
  return seasonal.length ? [...evergreen, ...seasonal] : products;
}

/** Types listed in a pure `product_type:"A" OR product_type:"B"` query, in order. */
function parseProductTypes(query?: string): string[] {
  if (!query) return [];
  // Operators are upper-case; type names are not ("Bar Carts and Servers" made
  // the old case-insensitive test reject Dining, whose page 1 was then 27 bar carts).
  if (/\bAND\b|[()]/.test(query.replace(/"[^"]*"/g, '""'))) return [];
  const parts = query.split(/\s+OR\s+/).map((s) => s.trim());
  const types: string[] = [];
  for (const part of parts) {
    const m = part.match(/^product_type:"([^"]+)"$|^product_type:([^\s"]+)$/i);
    if (!m) return [];
    types.push(m[1] || m[2]);
  }
  return types;
}

function buildProductTypeQuery(types: string[]) {
  return types.map((type) => `product_type:"${type}"`).join(" OR ");
}

// NOTE: there is no sale experience. Shopify supplies no real compareAtPrice
// above price anywhere in this catalogue, so a "sale" listing would be fiction.
// Sale routes redirect to the plain category in src/App.tsx.

/**
 * Where each product type's listing stopped, so Load More can continue all of
 * them. `taken` is how many of the type have been asked for so far.
 */
type TypeCursor = { type: string; endCursor: string | null; hasNextPage: boolean; taken: number };

const ITEMS_PER_PAGE = 40;
// First paint asks for one grid page plus spares for de-duplication; the pool
// then settles at three pages, which is also what each Load More adds.
const FIRST_SLICE = 48;
const POOL_SLICE = 120;
// Per-type depth the pool had when everything was fetched up front. Still how
// deep filter options are read and title filters search — see loadFacetPool.
const DEEP_PER_TYPE = 60;
// Stand in for "no limit on this side" in a price query. The floor is not 0: a
// query that names variants.price loses the listing's own `variants.price:>0`
// guard (withAvailability in shopify.ts), and >=0 would list the feed's $0
// products whenever it carries some. The ceiling is far above any product.
const OPEN_PRICE_MIN = 0.01;
const OPEN_PRICE_MAX = 10_000_000;

/**
 * A type's share of a multi-type page: the square root of its build-time
 * in-stock count. Equal shares opened Rugs with 29 bath mats, rug pads and
 * doormats in 40 cards (rugs are 92% of its stock) and gave Air Mattress, 3 in
 * stock, as many Bedroom slots as Standard Beds. Straight proportion overshoots
 * the other way: Outdoor would be 33 rugs in 40.
 */
const typeWeight = (type: string) => Math.sqrt(Math.max(typeStock(type) ?? 1, 1));

/**
 * Which type each of the next `count` cards comes from. A type's n-th product
 * belongs to round ceil((n - 0.5) / share), where share is its weight relative
 * to the heaviest type: the heaviest appears every round, a type a quarter its
 * weight every fourth. Within a round the category's own type order is kept.
 *
 * A type's first product is let in earlier: a quarter share (a sixteenth of the
 * heaviest type's stock) is enough for round 1, so a page opens with its
 * headline types in the category's order. Held to a half share like the rest,
 * All Products opened on its third type with the first sofa at card 20, and
 * Sofas kept the first Living Room card by 7 products of stock.
 */
function drawTypes(cursors: TypeCursor[], count: number): number[] {
  const weights = cursors.map((c) => typeWeight(c.type));
  const heaviest = Math.max(...weights);
  const taken = cursors.map((c) => c.taken);
  const order: number[] = [];
  while (order.length < count) {
    let pick = -1;
    let pickRound = Infinity;
    cursors.forEach((c, i) => {
      if (!c.hasNextPage) return;
      const round = Math.ceil(((taken[i] + (taken[i] === 0 ? 0.25 : 0.5)) * heaviest) / weights[i]);
      if (round < pickRound) {
        pick = i;
        pickRound = round;
      }
    });
    if (pick === -1) break;
    taken[pick]++;
    order.push(pick);
  }
  return order;
}

/** The draw that brings each of the `wanted` types up to `depth` products, a round at a time. */
function drawToDepth(cursors: TypeCursor[], depth: number, wanted: boolean[]): number[] {
  const order: number[] = [];
  for (let n = 0; n < depth; n++) {
    cursors.forEach((c, i) => {
      if (wanted[i] && c.hasNextPage && c.taken <= n) order.push(i);
    });
  }
  return order;
}

/**
 * Multi-type categories: fetch a slice per product type and interleave them. A
 * single widened query returns Shopify's default order, which clusters by type —
 * the widest type then fills the entire first page. `draw` names the type of
 * each card wanted, in order (drawTypes, drawToDepth).
 */
async function fetchInterleavedByType(
  cursors: TypeCursor[],
  draw: number[],
  options?: { sortKey?: string; reverse?: boolean },
): Promise<{ products: ShopifyProduct[]; cursors: TypeCursor[] }> {
  // 250 is the most Shopify returns per connection; no slice can exceed the batch.
  const order = draw.slice(0, 250);
  const wanted = cursors.map(() => 0);
  for (const i of order) wanted[i]++;
  const jobs = cursors.map((c, i) => ({ c, i })).filter(({ i }) => wanted[i] > 0);
  // One aliased request for the whole batch, each type matched exactly.
  const results = await fetchProductSlices(
    jobs.map(({ c, i }) => ({ query: exactTypeClause(c.type), first: wanted[i], after: c.endCursor })),
    options,
  );
  const byType: ShopifyProduct[][] = cursors.map(() => []);
  const next: TypeCursor[] = cursors.map((c) => ({ ...c }));
  jobs.forEach(({ c, i }, j) => {
    byType[i] = keepExactTypes(results[j].products, [c.type]);
    next[i] = { type: c.type, endCursor: results[j].pageInfo.endCursor, hasNextPage: results[j].pageInfo.hasNextPage, taken: c.taken + wanted[i] };
  });

  const seen = new Set<string>();
  const out: ShopifyProduct[] = [];
  const used = cursors.map(() => 0);
  for (const i of order) {
    const p = byType[i][used[i]++];
    if (!p || seen.has(p.node.id)) continue;
    seen.add(p.node.id);
    out.push(p);
  }
  // A date-ordered listing (New Arrivals) leads with the newest of the batch. In
  // draw order it opened with a January sofa — the newest of its type — ahead of
  // last week's dining tables.
  if (options?.sortKey === "CREATED_AT") {
    const sign = options.reverse ? -1 : 1;
    return { products: [...out].sort((a, b) => sign * (a.node.createdAt ?? "").localeCompare(b.node.createdAt ?? "")), cursors: next };
  }
  return { products: sinkSeasonal(out), cursors: next };
}

const firstCursors = (types: string[]): TypeCursor[] => types.map((type) => ({ type, endCursor: null, hasNextPage: true, taken: 0 }));

// ── Listing state in the address bar ────────────────────────────────────────
// Page, sort and filters are kept in the query string (replaced, never pushed),
// so Back from a product, a reload and a shared link return to the same view.
// They used to live in component state only: Back landed on page 1, unfiltered.
// The canonical stays /category/<slug>.
const URL_FILTER_KEYS: Record<string, string> = {
  productType: "type",
  material: "material",
  style: "style",
  color: "color",
  configuration: "config",
  size: "size",
};
const URL_SORTS: SortOption[] = ["price-asc", "price-desc", "newest"];

interface ListingState {
  sort: SortOption;
  price: [number, number] | null;
  filters: Record<string, string[]>;
  /** Zero-based grid page. */
  page: number;
}

function readListingState(sp: URLSearchParams): ListingState {
  const sort = sp.get("sort") as SortOption;
  const price = (sp.get("price") ?? "").match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
  const filters: Record<string, string[]> = {};
  for (const [key, param] of Object.entries(URL_FILTER_KEYS)) {
    const values = sp.getAll(param).map((v) => v.trim()).filter(Boolean);
    if (values.length) filters[key] = values;
  }
  const page = parseInt(sp.get("page") ?? "", 10);
  return {
    sort: URL_SORTS.includes(sort) ? sort : "default",
    price: price && Number(price[1]) <= Number(price[2]) ? [Number(price[1]), Number(price[2])] : null,
    filters,
    page: page > 1 ? page - 1 : 0,
  };
}

/** The state as query parameters, always in the same order. */
function listingParams(state: ListingState): [string, string][] {
  const out: [string, string][] = [];
  if (state.sort !== "default") out.push(["sort", state.sort]);
  if (state.price) out.push(["price", `${state.price[0]}-${state.price[1]}`]);
  for (const [key, param] of Object.entries(URL_FILTER_KEYS)) {
    for (const value of state.filters[key] ?? []) out.push([param, value]);
  }
  if (state.page > 0) out.push(["page", String(state.page + 1)]);
  return out;
}
const listingSearch = (state: ListingState) => new URLSearchParams(listingParams(state)).toString();

// Where each listing was scrolled to, for the return trip (sessionStorage, the
// last few paths). The browser's own restoration runs before the cards exist.
const SCROLL_SLOTS = "ll-listing-scroll";
type ScrollSlots = Record<string, { search: string; y: number }>;
const readScrollSlots = (): ScrollSlots => {
  try {
    return JSON.parse(sessionStorage.getItem(SCROLL_SLOTS) || "{}") || {};
  } catch {
    return {};
  }
};
function saveScrollSlot(path: string, search: string, y: number) {
  try {
    const slots = readScrollSlots();
    delete slots[path];
    slots[path] = { search, y };
    const keys = Object.keys(slots);
    for (const key of keys.slice(0, Math.max(0, keys.length - 12))) delete slots[key];
    sessionStorage.setItem(SCROLL_SLOTS, JSON.stringify(slots));
  } catch {
    /* storage unavailable */
  }
}

// What a build count covers where the page's name does not say it. New Arrivals
// lists the whole furniture range, newest first; All Products is the furniture,
// lighting and textile types (FURNITURE_QUERY_EXTENDED), not the whole catalogue
// the About page counts.
const COUNT_NOTE: Record<string, string> = {
  all: " in furniture, lighting, mirrors and home textiles",
  "new-arrivals": ", newest first",
};

/**
 * The price limits a query states, as a test on the price the card shows.
 * Shopify answers `variants.price:>=250 AND variants.price:<=600` with any
 * product that has a variant on each side — a rug at $37.99 and $276.99 — while
 * the card shows the lowest in-stock variant: "$250 to $600" sorted Low – High
 * opened at "From $37.99". Negating the other side in the query changes
 * nothing, so the limits are applied again here.
 */
function priceLimits(query?: string): ((p: ShopifyProduct) => boolean) | null {
  if (!query || !query.includes("variants.price:")) return null;
  const all = (re: RegExp) => Array.from(query.matchAll(re), (m) => Number(m[1]));
  const min = Math.max(-Infinity, ...all(/variants\.price:>=(\d+(?:\.\d+)?)/g));
  const max = Math.min(Infinity, ...all(/variants\.price:<=(\d+(?:\.\d+)?)/g));
  const below = Math.min(Infinity, ...all(/variants\.price:<(?!=)(\d+(?:\.\d+)?)/g));
  return (p) => {
    const price = parseFloat(listingPrice(p.node).price?.amount ?? "");
    return Number.isFinite(price) && price >= min && price <= max && price < below;
  };
}

/**
 * One page of a single-query listing. `types` are the product types the
 * category declares (none for a query that is not a plain type list). The query
 * already excludes the known look-alike types; if the exact-type check still
 * thins a page (a type added since the build), keep reading until a grid page
 * is full.
 */
async function fetchListingPage(
  query: string | undefined,
  types: string[],
  after: string | null,
  options?: { sortKey?: string; reverse?: boolean },
): Promise<FetchProductsResult> {
  let listed: ShopifyProduct[] = [];
  let cursor = after ?? undefined;
  const inBand = priceLimits(query);
  for (let page = 1; ; page++) {
    const result = await fetchProductsWithPagination(100, query, cursor, options);
    let rows = types.length ? keepExactTypes(result.products, types) : result.products;
    if (inBand) rows = rows.filter(inBand);
    listed = listed.concat(rows);
    if (listed.length >= ITEMS_PER_PAGE || !result.pageInfo.hasNextPage || page === 5) {
      return { products: listed, pageInfo: result.pageInfo };
    }
    cursor = result.pageInfo.endCursor ?? undefined;
  }
}


const Category = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  // Read once: afterwards the state leads and the address bar follows.
  const fromUrl = useRef(readListingState(searchParams)).current;
  const { data: promotion } = useActivePromotion();
  const category = getCategoryBySlug(slug || "");
  const subcategories = getDescendantCategories(slug || "");
  const parentCategory = category?.parent ? getCategoryBySlug(category.parent) : undefined;

  // An unknown slug must NOT be turned into a slug-derived text search: that
  // fabricated listings (e.g. /category/rugs returning 2,600 bath mats).
  // Unresolved slugs render the 404 page instead — see the early return below.
  const effectiveCategory = category ?? {
    slug: slug || "",
    name: "",
    description: "",
    query: undefined as string | undefined,
  };

  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  // Facet options are built from an unfiltered pool so narrowing to one colour
  // never empties the other filter groups.
  const [facetSource, setFacetSource] = useState<ShopifyProduct[]>([]);
  // True when that pool is the whole unfiltered listing, not its first slice.
  const [facetSourceWhole, setFacetSourceWhole] = useState(false);
  // Multi-type pages: DEEP_PER_TYPE products of every type, a few fields each,
  // read in the background once the first cards are in (loadFacetPool). `lists`
  // follow the category's type order.
  const [facetPool, setFacetPool] = useState<{ slug: string; lists: FacetProduct[][]; whole: boolean } | null>(null);
  // The slug that pool was last asked for, so one listing asks once.
  const facetPoolFor = useRef<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  // The category whose first load has finished: its sidebar then stays mounted
  // through its own reloads.
  const [readySlug, setReadySlug] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  // Multi-type categories page per product type (see fetchInterleavedByType).
  const [typeCursors, setTypeCursors] = useState<TypeCursor[] | null>(null);
  // Bumped by every (re)load so a Load More still in flight cannot append the
  // previous listing's products to the new one.
  const listingRef = useRef(0);
  // Cleared when a background top-up fails, so it is not retried in a loop.
  const topUpRef = useRef(true);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [sort, setSort] = useState<SortOption>(fromUrl.sort);
  const [priceFilter, setPriceFilter] = useState<[number, number] | null>(fromUrl.price);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(fromUrl.filters);
  const [expandedFilters, setExpandedFilters] = useState<Record<string, boolean>>({
    sort: true,
    price: true,
    productType: true,
    material: true,
    style: true,
    color: true,
  });
  const [currentPage, setCurrentPage] = useState(fromUrl.page);

  // The listing state the address bar holds, as this page last wrote or read
  // it. Anything else found there (a link with filters, Back/Forward, a plain
  // link to another category) is taken over as the state.
  const ownSearch = useRef(listingSearch(fromUrl));
  useEffect(() => {
    const inUrl = readListingState(searchParams);
    const search = listingSearch(inUrl);
    if (search === ownSearch.current) return;
    ownSearch.current = search;
    setSort(inUrl.sort);
    setPriceFilter(inUrl.price);
    setSelectedFilters(inUrl.filters);
    setCurrentPage(inUrl.page);
  }, [searchParams]);
  useEffect(() => {
    const state = { sort, price: priceFilter, filters: selectedFilters, page: currentPage };
    const search = listingSearch(state);
    if (search === ownSearch.current) return;
    ownSearch.current = search;
    const next = new URLSearchParams(window.location.search);
    for (const param of ["sort", "price", "page", ...Object.values(URL_FILTER_KEYS)]) next.delete(param);
    for (const [param, value] of listingParams(state)) next.append(param, value);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, priceFilter, selectedFilters, currentPage]);

  // Back to a listing returns to where it was left, once its cards are in.
  const restoreY = useRef<number | null>(null);
  useLayoutEffect(() => {
    const saved = navigationType === "POP" ? readScrollSlots()[pathname] : undefined;
    restoreY.current = saved && saved.search === ownSearch.current && saved.y > 0 ? saved.y : null;
    let frame = 0;
    const save = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        // Not while a return trip is pending: the page is still at its top.
        if (restoreY.current === null) saveScrollSlot(pathname, ownSearch.current, Math.round(window.scrollY));
      });
    };
    // The shopper moved first: leave the page where they put it.
    const cancel = () => { restoreY.current = null; };
    window.addEventListener("scroll", save, { passive: true });
    window.addEventListener("wheel", cancel, { passive: true });
    window.addEventListener("touchmove", cancel, { passive: true });
    window.addEventListener("keydown", cancel);
    // Removed in the commit that leaves the page, before the next page's
    // shorter document can report a clamped position.
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", save);
      window.removeEventListener("wheel", cancel);
      window.removeEventListener("touchmove", cancel);
      window.removeEventListener("keydown", cancel);
    };
  }, [pathname, navigationType]);

  const deepFacets = facetPool?.slug === slug ? facetPool : null;
  // What filter options and the price scale are read from.
  const facetProducts: FacetProduct[] = useMemo(
    () => (deepFacets ? deepFacets.lists.flat() : facetSource.length ? facetSource : products),
    [deepFacets, facetSource, products]
  );
  const facetsWhole = deepFacets ? deepFacets.whole : facetSourceWhole;

  const priceRange = useMemo(() => {
    if (facetProducts.length === 0) return { min: 0, max: 5000 };
    const prices = facetProducts.map((p) => parseFloat(p.node.priceRange.minVariantPrice.amount));
    const min = Math.floor(Math.min(...prices));
    // The slider (CategoryFilterSidebar) moves in $10 steps from the minimum.
    // The scale ends on one of those stops: ending between two, a thumb dragged
    // back to the far end rested a few dollars short of it and still counted
    // as a limit.
    return { min, max: min + Math.ceil((Math.max(...prices) - min) / 10) * 10 };
  }, [facetProducts]);
  // The scale spans only the prices in that pool, so a thumb left at either end
  // means "no limit on that side".
  const atScaleEnds = (range: [number, number]) => ({
    low: range[0] <= priceRange.min,
    high: range[1] >= priceRange.max,
  });
  const priceOpen = priceFilter ? atScaleEnds(priceFilter) : { low: true, high: true };

  // Facets and sorting are resolved by Shopify, not in the browser: filtering a
  // large category client-side only ever searched the first page.
  const facetClauses = useMemo(() => {
    // An open end sent as a bound silently dropped everything cheaper or dearer
    // than the pool the scale was drawn from.
    const price: [number, number] | null = priceFilter && [
      priceOpen.low ? OPEN_PRICE_MIN : priceFilter[0],
      priceOpen.high ? OPEN_PRICE_MAX : priceFilter[1],
    ];
    return buildFacetQuery(selectedFilters, price);
  }, [selectedFilters, priceFilter, priceOpen.low, priceOpen.high]);

  // The types a pure product_type category declares; empty for any other query.
  const spanTypes = useMemo(() => parseProductTypes(effectiveCategory.query), [effectiveCategory.query]);

  const serverQuery = useMemo(() => {
    if (!effectiveCategory.query) return undefined;
    // Made exact after the facets are added, so a Product Type picked in the
    // sidebar is exact too: Benches on "$100 to $250" was half shower benches.
    return exactTypeQuery(facetClauses ? `(${effectiveCategory.query}) AND ${facetClauses}` : effectiveCategory.query);
  }, [effectiveCategory.query, facetClauses]);

  const sortOptions = useMemo(
    () => mapSortToShopify(sort, effectiveCategory.sortKey, effectiveCategory.reverse),
    [sort, effectiveCategory.sortKey, effectiveCategory.reverse]
  );

  // Navigating to another category clears any narrowing from the previous one:
  // its address has none, and the effect above takes that over.
  useEffect(() => {
    setFacetSource([]);
    setFacetSourceWhole(false);
  }, [slug]);

  // Filter options and the price scale used to come from the 60 full cards per
  // type the page loaded before showing anything. Read from the first screen of
  // cards instead, Living Room offered 3 of its 6 configurations and no styles,
  // and an option that is not offered can never be picked. So the same depth is
  // read here with the slim selection, in one request, behind the first cards.
  const loadFacetPool = async (forSlug: string, types: string[], options?: { sortKey?: string; reverse?: boolean }) => {
    facetPoolFor.current = forSlug;
    try {
      const slices = await fetchFacetSlices(
        types.map((type) => ({ query: exactTypeClause(type), first: DEEP_PER_TYPE })),
        options,
      );
      if (facetPoolFor.current !== forSlug) return;
      const lists = slices.map((slice, i) => keepExactTypes(slice.products, [types[i]]));
      if (!lists.some((list) => list.length)) throw new Error("empty facet pool");
      setFacetPool({ slug: forSlug, lists, whole: !slices.some((slice) => slice.pageInfo.hasNextPage) });
    } catch {
      // The loaded cards keep supplying the options; the next unfiltered load asks again.
      if (facetPoolFor.current === forSlug) facetPoolFor.current = null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    listingRef.current++;
    topUpRef.current = true;
    setLoading(true);
    setLoadError(false);
    setHasNextPage(false);
    setEndCursor(null);
    setTypeCursors(null);
    setTotalCount(null);

    const load = async () => {
      // Unknown slug → nothing to fetch, the page renders 404.
      if (!effectiveCategory.query) {
        setProducts([]);
        setLoading(false);
        return;
      }
      try {
        // Multi-type categories (Rugs, Living Room, Decor, Bedroom…) are fetched
        // per product type and interleaved so no single type can dominate a
        // page of results. Once a facet or sort is chosen, Shopify does the work.
        if (spanTypes.length > 1 && !facetClauses && sort === "default") {
          // One screen of cards first: fetching 60 per type up front kept Living
          // Room on skeletons through 16 requests and ~3 MB of JSON. The effect
          // below tops the pool up once these are showing.
          const start = firstCursors(spanTypes);
          // A date-ordered page is sorted within each batch, so its first batch
          // is the whole pool: from 48 cards, a type whose newest piece is nine
          // months old still made page 1.
          const first = sortOptions?.sortKey === "CREATED_AT" ? POOL_SLICE : FIRST_SLICE;
          const { products: pool, cursors } = await fetchInterleavedByType(start, drawTypes(start, first), sortOptions);
          if (cancelled) return;
          setProducts(pool);
          setFacetSource(pool);
          const more = cursors.some((c) => c.hasNextPage);
          setFacetSourceWhole(!more);
          if (slug && more && facetPoolFor.current !== slug) loadFacetPool(slug, spanTypes, sortOptions);
          if (slug) recordCategoryStock(slug, pool.length);
          // Without these, Wall Art & Decor stopped at 69 of 1,222 pieces.
          setTypeCursors(cursors);
          setHasNextPage(more);
          return;
        }

        const result = await fetchListingPage(serverQuery, spanTypes, null, sortOptions);
        if (cancelled) return;
        // Only the default order: in a list the shopper sorted by price or date,
        // a $35.99 Christmas item sank below one at $1,967.99.
        const loaded = sort === "default" ? sinkSeasonal(result.products) : result.products;
        setProducts(loaded);
        // Not on a multi-type page: its pool is the default view's, and a
        // sorted first page would shrink the options to the 100 cheapest.
        if (!facetClauses && spanTypes.length < 2) {
          setFacetSource(loaded);
          setFacetSourceWhole(!result.pageInfo.hasNextPage);
        }
        // In-stock count is derived from what the products connection actually
        // returned — fetchProductCount uses the `search` connection, which
        // returns 0 for available_for_sale filters.
        if (slug && !facetClauses) recordCategoryStock(slug, loaded.length);
        setHasNextPage(result.pageInfo.hasNextPage);
        setEndCursor(result.pageInfo.endCursor);
      } catch {
        // A failed request is never reported as "out of stock" — see round 19.
        if (cancelled) return;
        setProducts([]);
        setLoadError(true);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setReadySlug(slug ?? null);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug, effectiveCategory.query, spanTypes, serverQuery, facetClauses, sort, sortOptions, reloadKey]);

  const loadMore = async (draw?: number[], background = false) => {
    if (loadingMore) return;
    const listing = listingRef.current;
    if (typeCursors) {
      setLoadingMore(true);
      try {
        const { products: more, cursors } = await fetchInterleavedByType(typeCursors, draw ?? drawTypes(typeCursors, POOL_SLICE), sortOptions);
        if (listing !== listingRef.current) return;
        setProducts((prev) => {
          const seen = new Set(prev.map((p) => p.node.id));
          return [...prev, ...more.filter((p) => !seen.has(p.node.id))];
        });
        setTypeCursors(cursors);
        setHasNextPage(cursors.some((c) => c.hasNextPage));
        topUpRef.current = true;
      } catch {
        // silent — and a failed background top-up is not retried
        if (background) topUpRef.current = false;
      } finally {
        setLoadingMore(false);
      }
      return;
    }
    if (!endCursor) return;
    setLoadingMore(true);
    try {
      const result = await fetchListingPage(serverQuery, spanTypes, endCursor, sortOptions);
      if (listing !== listingRef.current) return;
      setProducts((prev) => [...prev, ...result.products]);
      setHasNextPage(result.pageInfo.hasNextPage);
      setEndCursor(result.pageInfo.endCursor);
    } catch {
      // silent
    } finally {
      setLoadingMore(false);
    }
  };

  // Configuration and Size are parsed from titles and matched in the browser,
  // against whatever is loaded.
  const titleFilters = Object.entries(selectedFilters).filter(([key, values]) => values.length > 0 && !isServerFacet(key));
  const hasTitleFilter = titleFilters.length > 0;

  // Multi-type pages paint from one screen of cards, then fill the pool to a few
  // pages in the background so pagination has something to work with. The rest
  // comes on demand: Load More, a server facet or sort (Shopify takes over), or
  // a title filter, which needs its products loaded.
  useEffect(() => {
    if (loading || loadingMore || !typeCursors || !hasNextPage || !topUpRef.current) return;
    if (hasTitleFilter) {
      // The options were read from the first DEEP_PER_TYPE of every type, so
      // that is how deep the filter has to search — but only in the types the
      // pool found a match in (all of them until the pool is in). U-Shaped on
      // Living Room is one request for its sectionals, not 60 of all 16 types.
      const wanted = typeCursors.map(
        (_, i) => !deepFacets || (deepFacets.lists[i] ?? []).some((p) => titleFilters.every(([key, values]) => productMatchesFilter(p, key, values))),
      );
      const draw = drawToDepth(typeCursors, DEEP_PER_TYPE, wanted);
      if (draw.length) loadMore(draw, true);
      return;
    }
    // Only while a grid page or more is missing: a batch can come back a card or
    // two short (duplicates, a type running out), which is not worth a request.
    const missing = POOL_SLICE - products.length;
    if (missing >= ITEMS_PER_PAGE) loadMore(drawTypes(typeCursors, missing), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadingMore, typeCursors, hasNextPage, products.length, selectedFilters, deepFacets]);

  // Remember the last category viewed so the cart can offer a way back.
  useEffect(() => {
    if (!slug) return;
    try {
      localStorage.setItem(
        "ll-last-category",
        JSON.stringify({ slug, name: effectiveCategory.name })
      );
    } catch { /* storage unavailable */ }
  }, [slug, effectiveCategory.name]);

  // Extract dynamic filter groups from products
  const filterGroups: FilterGroup[] = useMemo(() => {
    const pool = facetProducts;
    const extracted = extractFiltersFromProducts(pool, { optionColors: false });
    const groups: FilterGroup[] = [];
    // The number beside a facet reads as a total for the category. Counted from
    // a pool that is only a sample it is not one ("Wood (14)" on a page of
    // 3,431), so it is shown only when the pool is the whole listing. Without
    // the numbers, most-common-first is an order with no visible reason that
    // moves the options whenever the pool changes, so they are listed A to Z.
    const counted = (items: [string, number][], whole = facetsWhole): [string, number][] =>
      whole
        ? items
        : items
            .map(([value]): [string, number] => [value, 0])
            .sort((a, b) => a[0].localeCompare(b[0], "en", { numeric: true }));
    // A multi-type category knows its types and their build-time in-stock
    // counts without loading anything.
    const declaredTypes = spanTypes
      .map((type): [string, number] => [type, typeStock(type) ?? 0])
      .filter(([, count]) => count > 0);

    // The shop's own name for a supplier type ("Coat Hangers" is the Coat Racks
    // & Hooks category); the supplier type stays the filter's value.
    const typeLabels = (items: [string, number][]) => Object.fromEntries(items.map(([type]) => [type, productTypeLabel(type)]));

    // Product Type — the static list for the curated pages that span those
    // types (All Products, Best Sellers, New Arrivals), less the types with
    // nothing in stock. A price band or a style edit spans no declared types:
    // offered the whole list, 20 of the 31 types on "Under $100" returned
    // nothing, so those pages offer the types their loaded products have.
    if (effectiveCategory.useStaticFilters && spanTypes.length > 1) {
      groups.push({
        label: "Product Type",
        key: "productType",
        items: getStaticProductTypeFilters().filter(([label]) => (typeStock(getShopifyProductTypeFromLabel(label) ?? label) ?? 1) > 0),
      });
    } else if (declaredTypes.length > 1) {
      const items = declaredTypes.sort((a, b) => b[1] - a[1]).slice(0, 20);
      groups.push({ label: "Product Type", key: "productType", items, labels: typeLabels(items) });
    } else if (extracted.productTypes.size > 1) {
      const items = counted(Array.from(extracted.productTypes.entries()).sort((a, b) => b[1] - a[1]).slice(0, 20));
      groups.push({ label: "Product Type", key: "productType", items, labels: typeLabels(items) });
    }

    // Material
    if (extracted.materials.size > 0) {
      groups.push({
        label: "Material",
        key: "material",
        items: counted(Array.from(extracted.materials.entries())
          .filter(([, count]) => count >= 2)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 15)),
      });
    }

    // Style
    if (extracted.styles.size > 0) {
      groups.push({
        label: "Style",
        key: "style",
        items: counted(Array.from(extracted.styles.entries())
          .filter(([, count]) => count >= 2)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)),
      });
    }

    // Color
    if (extracted.colors.size > 0) {
      groups.push({
        label: "Color",
        key: "color",
        items: counted(Array.from(extracted.colors.entries())
          .filter(([, count]) => count >= 2)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 15)),
      });
    }

    // Configuration & Size — parsed from real product titles, no new data needed.
    // They are matched in the browser, so they are offered from what it can
    // search: the pool while the page lists per type (the top-up effect loads
    // that depth), the loaded products once a sort or facet has handed a
    // multi-type page to a single Shopify query.
    const singleQuery = spanTypes.length > 1 && !typeCursors;
    const titlePool: FacetProduct[] = singleQuery ? products : pool;
    const titleWhole = singleQuery ? !hasNextPage : facetsWhole;
    const configCounts = new Map<string, number>();
    const sizeCounts = new Map<string, number>();
    for (const p of titlePool) {
      const attrs = parseTitleAttributes(p.node.title);
      for (const c of attrs.configs) configCounts.set(c, (configCounts.get(c) || 0) + 1);
      if (attrs.pieceCount) sizeCounts.set(attrs.pieceCount, (sizeCounts.get(attrs.pieceCount) || 0) + 1);
    }
    // A ticked option stays listed when the loaded products no longer hold it,
    // so it can still be unticked there.
    const ticked = (key: string, counts: Map<string, number>) => {
      for (const value of selectedFilters[key] ?? []) if (!counts.has(value)) counts.set(value, 0);
      return (selectedFilters[key] ?? []).length > 0;
    };
    const configTicked = ticked("configuration", configCounts);
    const sizeTicked = ticked("size", sizeCounts);

    if (configCounts.size > 1 || configTicked) {
      groups.push({
        label: "Configuration",
        key: "configuration",
        items: counted(Array.from(configCounts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10), titleWhole),
      });
    }

    if (sizeCounts.size > 1 || sizeTicked) {
      groups.push({
        label: "Size",
        key: "size",
        items: counted(Array.from(sizeCounts.entries()).sort(
          (a, b) => parseInt(a[0]) - parseInt(b[0])
        ).slice(0, 12), titleWhole),
      });
    }

    return groups;
  }, [products, facetProducts, facetsWhole, spanTypes, typeCursors, hasNextPage, selectedFilters, effectiveCategory.useStaticFilters]);

  const activeFilterCount =
    (priceFilter ? 1 : 0) +
    Object.values(selectedFilters).filter(v => v.length > 0).length;

  const filteredAndSorted = useMemo(() => {
    let result = [...products];

    // Price, product type, material, style and colour are already applied by
    // Shopify. Configuration and Size are parsed out of titles, so they stay here.
    for (const [groupKey, values] of Object.entries(selectedFilters)) {
      if (values.length > 0 && !isServerFacet(groupKey)) {
        result = result.filter((p) => productMatchesFilter(p, groupKey, values));
      }
    }

    // Only a real Shopify compare-at price counts as a discount.
    const getDiscountPercent = (p: ShopifyProduct) => {
      const price = parseFloat(p.node.priceRange?.minVariantPrice?.amount || "0");
      const compareAt = parseFloat(p.node.compareAtPriceRange?.minVariantPrice?.amount || "0");
      if (price > 0 && compareAt > price) return ((compareAt - price) / compareAt) * 100;
      return 0;
    };

    // Price/name/newest ordering comes back already sorted from Shopify
    // (sortKey/reverse), so re-sorting the page here would fight the API.
    if (sort === "discount-desc") {
      result.sort((a, b) => getDiscountPercent(b) - getDiscountPercent(a));
    }

    // Move products without images to the end (only when using default sort)
    if (sort === "default") {
      result.sort((a, b) => {
        const aHasImage = (a.node.images?.edges?.length || 0) > 0 ? 0 : 1;
        const bHasImage = (b.node.images?.edges?.length || 0) > 0 ? 0 : 1;
        return aHasImage - bHasImage;
      });
    }

    // Interleave products by type for generic categories to create variety.
    // Not the per-type listings: they arrive interleaved, and re-dealing the
    // whole pool would reshuffle the first page each time more of it loads.
    if (effectiveCategory.useStaticFilters && sort === "default" && !typeCursors) {
      result = interleaveByType(result);
    }

    // Only what a shopper could not tell apart (same title, price and photo):
    // the rail rule hid 577 of 861 chandeliers behind 284 cards.
    return dedupeProducts(result, { listing: true });
  }, [products, sort, selectedFilters, effectiveCategory.useStaticFilters, typeCursors]);

  // Unfiltered: the build-time in-stock count the intro states, not the size of
  // the first loaded page ("69 products" above "1,222 pieces in stock").
  const unfiltered = !facetClauses && Object.values(selectedFilters).every((v) => !v?.length);
  // All Products, Best Sellers and New Arrivals are not counted per category at
  // build time (-1), but each of their types is. The sum is the count the
  // Product Type list already states, and unlike the number of cards loaded
  // ("47+ products", then "114+") it is there from the first paint. Zero
  // without the generated table, which leaves the loaded count below.
  const builtTotal = useMemo(() => {
    const measured = CATEGORY_STOCK[effectiveCategory.slug] ?? 0;
    if (measured >= 0 || spanTypes.length < 2) return Math.max(measured, 0);
    return spanTypes.reduce((sum, type) => sum + (typeStock(type) ?? 0), 0);
  }, [effectiveCategory.slug, spanTypes]);
  // Once the whole listing is loaded its own length is the count: that is what
  // can be reached, and the build count is hours old.
  const wholeLoaded = !loading && !loadError && !hasNextPage;
  const builtCount = unfiltered && !wholeLoaded ? builtTotal : 0;
  // The sidebar's "N items" and the headline label state the same number.
  const displayedCount = totalCount ?? Math.max(builtCount, filteredAndSorted.length);

  const displayedCountLabel = useMemo(() => {
    if (totalCount !== null) return `${totalCount.toLocaleString()} product${totalCount !== 1 ? "s" : ""}`;
    if (builtCount > filteredAndSorted.length) {
      const note = effectiveCategory.slug !== "new-arrivals" || sort === "default" ? COUNT_NOTE[effectiveCategory.slug] ?? "" : "";
      return `${builtCount.toLocaleString("en-US")} products${note}`;
    }
    // For curated categories with skipCount, show loaded count + "+"
    const count = filteredAndSorted.length;
    const suffix = hasNextPage ? "+" : "";
    return `${count.toLocaleString("en-US")}${suffix} product${count !== 1 ? "s" : ""}`;
  }, [totalCount, filteredAndSorted.length, hasNextPage, builtCount, effectiveCategory.slug, sort]);

  // The grid page shown: a page restored from the address bar waits here until
  // its products are loaded.
  const totalPages = Math.ceil(filteredAndSorted.length / ITEMS_PER_PAGE);
  const shownPage = Math.min(currentPage, Math.max(totalPages - 1, 0));

  // A restored page beyond what the first load brought (Back to page 4, a
  // reloaded address): read on, a few requests at most.
  const restoreLoads = useRef(0);
  useEffect(() => {
    if (loading || loadingMore || !hasNextPage || currentPage === 0 || currentPage < totalPages || restoreLoads.current >= 4) return;
    restoreLoads.current++;
    loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadingMore, hasNextPage, currentPage, totalPages]);

  // Back to this listing: scroll to where it was left, once the page is tall
  // enough or nothing more is coming (a multi-type pool fills in behind the
  // first cards).
  useEffect(() => {
    const y = restoreY.current;
    if (y === null || loading) return;
    const reachable = document.documentElement.scrollHeight - window.innerHeight >= y;
    const settled = !loadingMore && (!hasNextPage || filteredAndSorted.length >= (currentPage + 1) * ITEMS_PER_PAGE);
    if (!reachable && !settled) return;
    restoreY.current = null;
    window.scrollTo(0, y);
  }, [loading, loadingMore, hasNextPage, filteredAndSorted.length, currentPage]);

  const clearAllFilters = () => {
    setPriceFilter(null);
    setSelectedFilters({});
    setCurrentPage(0);
  };

  const toggleFilter = (section: string) => {
    setExpandedFilters((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleFilterValue = (groupKey: string, value: string) => {
    // Configuration and Size narrow the loaded list without a reload, so nothing
    // else returns the grid to page 1: chosen from page 3 they left it empty.
    setCurrentPage(0);
    setSelectedFilters((prev) => {
      const current = prev[groupKey] || [];
      const newValues = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return { ...prev, [groupKey]: newValues };
    });
  };

  const sidebarProps = {
    totalCount: displayedCount,
    sort,
    onSortChange: (s: SortOption) => { setSort(s); setCurrentPage(0); },
    priceRange,
    priceFilter,
    // Both thumbs back at the ends is no filter at all, not an open-ended one
    // that still hands the page to a single query.
    onPriceFilterChange: (f: [number, number] | null) => {
      const ends = f && atScaleEnds(f);
      setPriceFilter(ends && ends.low && ends.high ? null : f);
      setCurrentPage(0);
    },
    filterGroups,
    selectedFilters,
    onToggleFilter: toggleFilterValue,
    activeFilterCount,
    onClearAll: clearAllFilters,
    expandedFilters,
    onToggleSection: toggleFilter,
  };

  const breadcrumbItems = [
    ...(effectiveCategory.parent
      ? [{ label: getCategoryBySlug(effectiveCategory.parent)?.name || effectiveCategory.parent, href: `/category/${effectiveCategory.parent}` }]
      : []),
    { label: effectiveCategory.name },
  ];

  // Build-time in-stock count, the same number the prerendered head states.
  // -1 (not measurable from product type alone) and 0 omit the count.
  const stockCount = Math.max(0, CATEGORY_STOCK[effectiveCategory.slug] ?? 0);
  // Read from the first grid page only: it is what is on screen, and it stays
  // put while more of the listing loads behind it, so the intro never reflows.
  const categoryFacts = extractCategoryFacts(products.slice(0, ITEMS_PER_PAGE), stockCount);
  const seoTitle = categoryHeadTitle(effectiveCategory);
  const seoDescription = categoryHeadDescription(effectiveCategory, stockCount);
  // Size-led categories link to their free calculator (scripts/tools-content.mjs).
  const sizingTool = CATEGORY_TOOL[category?.slug ?? ""];
  const seoIntro = categoryIntro(effectiveCategory.name, effectiveCategory.description, categoryFacts);
  const canonicalUrl = `https://loftloom.com/category/${effectiveCategory.slug}`;

  // Categories with a genuinely small catalog: hide the count line and
  // pagination so a single short row of results still reads well.
  const THIN_CATEGORY_MAX = 25;
  const isThinCategory =
    !hasNextPage &&
    (totalCount ?? filteredAndSorted.length) < THIN_CATEGORY_MAX;

  // Load More (fetch next API page). Shared by both views: the list view had
  // none, so it stopped at whatever the first load brought.
  const loadMoreButton = hasNextPage && !loading && (
    <div className="flex justify-center mt-6">
      <Button
        variant="outline"
        onClick={() => loadMore()}
        disabled={loadingMore}
        className="px-8 py-2.5 rounded-full text-sm font-medium"
      >
        {loadingMore ? (
          <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading...</>
        ) : (
          "Load More Products"
        )}
      </Button>
    </div>
  );

  const filtersMounted = readySlug === slug && (loading || products.length > 0 || activeFilterCount > 0);

  // Unresolved slug → real 404 (noindex), never a fabricated listing.
  if (!category) return <NotFound />;

  return (
    <div className="min-h-screen flex flex-col">
      <OGTags
        title={seoTitle}
        description={seoDescription}
        url={canonicalUrl}
        canonical={canonicalUrl}
      />

      {/* The blocks the prerendered page carries, from the same builder
          (scripts/page-schemas.mjs): the breadcrumb at once, and the
          CollectionPage with its product list once products are in — never an
          empty one. */}
      <Helmet>
        {categorySchemas(effectiveCategory, {
          parent: effectiveCategory.parent ? getCategoryBySlug(effectiveCategory.parent) ?? null : null,
          items: loading ? [] : products.map((p) => ({ handle: p.node.handle, title: stripVendors(p.node.title) || p.node.handle })),
          total: CATEGORY_STOCK[effectiveCategory.slug] ?? 0,
        }).map((schema) => (
          <script key={schema["@type"]} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>
      <AnnouncementBar />
      <Header />
      <main className="flex-1">
        <Breadcrumbs items={breadcrumbItems} />

        <CategoryRoomNav />

            {/* Category title */}
            <div className="container pb-2 pt-6 md:pt-8">
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="page-title text-4xl"
              >
                <span className="font-normal">{effectiveCategory.name.split(" ")[0]}</span>{" "}
                <span className="italic text-primary font-normal">{effectiveCategory.name.split(" ").slice(1).join(" ") || ""}</span>
              </motion.h1>
              {/* The line is reserved while loading: inserting it after the products
                  arrived pushed the grid down (desktop CLS 0.11). */}
              {(loading || (products.length > 0 && !isThinCategory)) && (
                <p className="text-sm text-muted-foreground mt-1 min-h-[1.25rem]">
                  {loading ? "\u00a0" : displayedCountLabel}
                </p>
              )}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="text-[14px] text-foreground/75 mt-3 max-w-[680px] leading-relaxed"
              >
                {seoIntro}
              </motion.p>
              {sizingTool && (
                <Link
                  to={sizingTool.path}
                  className="mt-2 inline-flex items-center gap-1.5 text-[13px] text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
                >
                  <Ruler className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {sizingTool.prompt}
                </Link>
              )}
            </div>

            {/* Subcategory links */}
            {subcategories.length > 0 && (
              <div className="container pb-4">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-1 md:mx-0 md:px-0 md:pb-0 md:flex-wrap md:overflow-visible"
                >
                  {/* One swipeable row on phones: wrapped, a big room's 10–15 links
                      pushed the first products below the fold. */}
                  {subcategories.map((sub) => (
                    <Link
                      key={sub.slug}
                      to={`/category/${sub.slug}`}
                      className="shrink-0 whitespace-nowrap text-xs font-medium text-muted-foreground hover:text-foreground border border-border px-3 py-1.5 transition-colors hover:border-foreground/40"
                    >
                      {sub.name}
                    </Link>
                  ))}
                </motion.div>
              </div>
            )}

            {/* Active filters bar */}
            {activeFilterCount > 0 && (
              <div className="container pb-2">
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="flex items-center gap-2 flex-wrap"
                >
                  <span className="text-xs text-muted-foreground">Active filters:</span>
                  {priceFilter && (
                    <button
                      onClick={() => { setPriceFilter(null); setCurrentPage(0); }}
                      className="inline-flex items-center gap-1 text-xs bg-secondary text-secondary-foreground px-2.5 py-1 rounded-full hover:bg-secondary/80 transition-colors"
                    >
                      {/* An open end is not a bound, so it is not printed as one. */}
                      {priceOpen.low
                        ? priceOpen.high ? "Any price" : `Up to $${priceFilter[1]}`
                        : priceOpen.high ? `From $${priceFilter[0]}` : `$${priceFilter[0]} – $${priceFilter[1]}`}
                      <X className="h-3 w-3" />
                    </button>
                  )}
                  {Object.entries(selectedFilters).flatMap(([groupKey, values]) =>
                    values.map((value) => (
                      <button
                        key={`${groupKey}-${value}`}
                        onClick={() => toggleFilterValue(groupKey, value)}
                        className="inline-flex items-center gap-1 text-xs bg-secondary text-secondary-foreground px-2.5 py-1 rounded-full hover:bg-secondary/80 transition-colors"
                      >
                        {groupKey === "productType" ? productTypeLabel(value) : value}
                        <X className="h-3 w-3" />
                      </button>
                    ))
                  )}
                  <button onClick={clearAllFilters} className="text-xs text-primary hover:underline ml-1">
                    Clear all
                  </button>
                </motion.div>
              </div>
            )}

            {/* Mobile filter toggle. Kept through reloads and when filters return
                nothing: it used to vanish with the results it could bring back. */}
            {filtersMounted && (
              <div className="container pb-2 flex items-center gap-2">
                <div className="md:hidden flex-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs font-medium gap-1.5 h-9 w-full justify-center"
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                    Sort & Filter
                    {activeFilterCount > 0 && (
                      <span className="bg-primary text-primary-foreground rounded-full h-4 w-4 text-[9px] flex items-center justify-center ml-1">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </div>
                <div className="hidden md:flex items-center gap-1 ml-auto">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-1.5 rounded transition-colors ${viewMode === "grid" ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    aria-label="Grid view"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-1.5 rounded transition-colors ${viewMode === "list" ? "bg-foreground/10 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    aria-label="List view"
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Mobile filter panel */}
            <AnimatePresence>
              {sidebarOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="md:hidden container pb-4 overflow-hidden"
                >
                  <div className="border border-border p-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-bold">Sort & Filter</span>
                      <button onClick={() => setSidebarOpen(false)} aria-label="Close filters">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <CategoryFilterSidebar {...sidebarProps} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Content: sidebar + grid */}
            <div className="container pb-16 pt-4">
              <div className="flex gap-10">
                {/* Desktop sidebar. Unmounted on every reload it dropped the price
                    thumb mid-drag and lost its scroll position. */}
                {filtersMounted && (
                  <aside className="hidden md:block w-52 flex-shrink-0">
                    <CategoryFilterSidebar {...sidebarProps} />
                  </aside>
                )}

                {/* Product grid */}
                <div className="flex-1">
                  {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-6 md:gap-x-4 md:gap-y-8">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <ProductCardSkeleton key={i} />
                      ))}
                    </div>
                  ) : filteredAndSorted.length === 0 ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-20"
                    >
                      <p className="text-muted-foreground text-sm">
                        {loadError
                          ? "We couldn't load this category — try again."
                          : activeFilterCount > 0
                            ? "No products match your filters."
                            : "Everything in this category is currently out of stock."}
                      </p>
                      {loadError ? (
                        <button
                          onClick={() => setReloadKey((k) => k + 1)}
                          className="text-primary hover:underline text-sm mt-2"
                        >
                          Try again
                        </button>
                      ) : activeFilterCount > 0 ? (
                        <button onClick={clearAllFilters} className="text-primary hover:underline text-sm mt-2">
                          Clear filters
                        </button>
                      ) : (
                        <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
                          {parentCategory && (
                            <Link to={`/category/${parentCategory.slug}`} className="text-primary hover:underline">
                              Browse {parentCategory.name}
                            </Link>
                          )}
                          <Link to="/categories" className="text-primary hover:underline">
                            All categories
                          </Link>
                        </div>
                      )}
                    </motion.div>
                  ) : viewMode === "list" ? (
                    <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                      className="space-y-4"
                    >
                      {filteredAndSorted.map((product) => {
                        const img = product.node.images?.edges?.[0]?.node;
                        const { price, from: priceIsFrom } = listingPrice(product.node);
                        return (
                          <motion.div
                            key={product.node.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex gap-4 p-3 border border-border hover:border-foreground/20 transition-colors group"
                          >
                            <Link to={`/product/${product.node.handle}`} className="w-28 h-28 bg-white flex-shrink-0 overflow-hidden">
                              {img && (
                                <img
                                  src={getShopifyImageUrl(img.url, 200)}
                                  alt={productImageAlt(product.node.title, { productType: product.node.productType })}
                                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                  loading="lazy"
                                />
                              )}
                            </Link>
                            <div className="flex-1 min-w-0 flex flex-col justify-center">
                              <Link to={`/product/${product.node.handle}`}>
                                <h3 className="text-sm font-medium text-foreground line-clamp-2 hover:text-primary transition-colors">
                                  {cleanProductTitle(product.node.title, product.node.productType)}
                                </h3>
                              </Link>
                              {product.node.productType && (
                                <p className="text-xs text-muted-foreground mt-1">{product.node.productType}</p>
                              )}
                              {price && (
                                <p className="text-sm font-semibold mt-1.5">
                                  {priceIsFrom && "From "}
                                  {formatPrice(price.amount, price.currencyCode)}
                                </p>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </motion.div>
                    {loadMoreButton}
                    </>
                  ) : (() => {
                    const paginatedProducts = filteredAndSorted.slice(shownPage * ITEMS_PER_PAGE, (shownPage + 1) * ITEMS_PER_PAGE);
                    return (
                      <>
                        {/* scroll-mt: a page change scrolls here, and the sticky
                            header covered the first row. */}
                        <div
                          id="product-grid"
                          className="scroll-mt-20 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-6 md:gap-x-4 md:gap-y-8"
                        >
                          {(() => {
                            const gridNames = uniqueDisplayNames(paginatedProducts);
                            return paginatedProducts.map((product, i) => (
                              <ScrollRevealCard key={product.node.id} index={i % 4}>
                                <ProductCard product={product} displayName={gridNames[product.node.handle]} />
                              </ScrollRevealCard>
                            ));
                          })()}
                        </div>

                        {!isThinCategory && (
                          <ProductPagination currentPage={shownPage} totalPages={totalPages} onPageChange={setCurrentPage} scrollTargetId="product-grid" />
                        )}

                        {shownPage === totalPages - 1 && loadMoreButton}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Journal guides for this category — renders nothing when empty. */}
            <GuideLinks categorySlug={category?.slug} categoryName={category?.name} />
      </main>
      <Footer />
    </div>
  );
};

export default Category;
